import {ChevronDownIcon, SearchIcon, XIcon} from '@heroicons/react/outline';
import {useEffect, useRef, useState} from 'react';

type Option = {key: string; value: string};

interface BaseProps {
  options: Option[];
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface MultipleProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type SearchableDropdownProps = SingleProps | MultipleProps;

const SearchableDropdown = ({
  options,
  label,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  disabled = false,
  error,
  className,
  multiple,
  value,
  onChange,
}: SearchableDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filtered = options.filter((opt) =>
    opt.key.toLowerCase().includes(search.toLowerCase()),
  );

  // Focusing a DOM node is a real side effect, so it stays in an effect. The
  // search reset moved to the toggle handler that causes it.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The search box only exists while the dropdown is open, so clearing on
  // every toggle is indistinguishable from clearing on open alone.
  const handleToggle = () => {
    setSearch('');
    setIsOpen((prev) => !prev);
  };

  const isSelected = (val: string) =>
    multiple ? value.includes(val) : value === val;

  const handleSelect = (val: string) => {
    if (multiple) {
      const current = value;
      const next = current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val];
      onChange(next);
    } else {
      onChange(val);
      setIsOpen(false);
    }
  };

  const handleSelectAll = () => {
    (onChange as (v: string[]) => void)(options.map((o) => o.value));
  };

  const handleClear = () => {
    if (multiple) {
      onChange([]);
    } else {
      onChange('');
    }
  };

  const triggerLabel = () => {
    if (multiple) {
      const selected = value;
      if (selected.length === 0) {
        return placeholder;
      }
      if (selected.length === 1) {
        return options.find((o) => o.value === selected[0])?.key ?? placeholder;
      }
      return `${selected.length} selected`;
    }
    return options.find((o) => o.value === value)?.key ?? placeholder;
  };

  const hasValue = multiple ? value.length > 0 : Boolean(value);

  return (
    <div ref={containerRef} className={`relative w-full ${className ?? ''}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          {label}
        </label>
      )}

      <button
        type="button"
        disabled={disabled}
        onClick={handleToggle}
        className={`flex items-center justify-between w-full px-3 py-2 text-sm border rounded-md bg-white dark:bg-transparent text-left transition-colors
          ${error ? 'border-red-500' : 'border-gray-300 dark:border-dark-borderHeavy'}
          ${isOpen ? 'ring-1 ring-indigo-500 border-indigo-500' : 'focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'}
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${hasValue ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}
      >
        <span className="truncate">{triggerLabel()}</span>
        <span className="flex items-center gap-1 ml-2 shrink-0">
          {hasValue && !disabled && (
            <XIcon
              className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
          <ChevronDownIcon
            className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-dark-inputBg border border-gray-300 dark:border-dark-borderHeavy rounded-md shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-dark-borderHeavy">
            <div className="relative">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-dark-borderHeavy rounded-md bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>

          {multiple && (
            <div className="flex gap-2 px-2 py-1.5 border-b border-gray-200 dark:border-dark-borderHeavy">
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={value.length === options.length}
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-40 disabled:no-underline"
              >
                Select all
              </button>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <button
                type="button"
                onClick={handleClear}
                disabled={value.length === 0}
                className="text-xs text-gray-500 dark:text-gray-400 hover:underline disabled:opacity-40 disabled:no-underline"
              >
                Clear
              </button>
            </div>
          )}

          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">
                No options found
              </li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors
                      ${
                        isSelected(opt.value)
                          ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300'
                          : 'text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-dark-borderMid'
                      }`}
                  >
                    {multiple && (
                      <span
                        className={`flex-shrink-0 w-4 h-4 border-2 rounded flex items-center justify-center transition-colors
                          ${isSelected(opt.value) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 dark:border-gray-500'}`}
                      >
                        {isSelected(opt.value) && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </span>
                    )}
                    <span className="truncate">{opt.key}</span>
                    {!multiple && isSelected(opt.value) && (
                      <svg
                        className="ml-auto w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
    </div>
  );
};

export default SearchableDropdown;
